document.documentElement.classList.remove('no-js');

if (window.Shopify && window.Shopify.designMode) {
  document.documentElement.classList.add('shopify-design-mode');
}

/* The sentinel lives in the layout, the header in a section the Theme Editor can
   replace, so the header is looked up on every change rather than captured. */
const headerSentinel = document.querySelector('[data-header-sentinel]');

if (headerSentinel && 'IntersectionObserver' in window) {
  const condenseObserver = new IntersectionObserver(
    ([entry]) => {
      const condensed = !entry.isIntersecting;
      document.querySelector('[data-header]')?.classList.toggle('is-condensed', condensed);
      /* The announcement strip is a sibling section, so the state also lives on
         the document element where any part of the header group can read it. */
      document.documentElement.classList.toggle('header-condensed', condensed);
    },
    { threshold: 0 }
  );
  condenseObserver.observe(headerSentinel);
}

/* Language. An explicit choice always wins: once the visitor picks a language, or
   once the browser match has fired, we never switch for them again. Submitting
   Shopify's own localization form keeps the locale URLs correct. */

const LOCALE_CHOICE_KEY = 'savory-crest:locale-choice';

function rememberLocaleChoice(value) {
  try {
    localStorage.setItem(LOCALE_CHOICE_KEY, value);
  } catch (error) {
    /* Storage can be unavailable in private modes; the switcher still works. */
  }
}

function previewLanguageUrl(switcher, option) {
  if (!window.location.hostname.endsWith('.shopifypreview.com')) return null;

  const currentRoot = switcher.dataset.currentLanguageRoot || '/';
  const targetRoot = option.dataset.languageRoot || '/';
  let path = window.location.pathname;

  if (currentRoot !== '/' && (path === currentRoot || path.startsWith(`${currentRoot}/`))) {
    path = path.slice(currentRoot.length) || '/';
  }

  const targetPath = targetRoot === '/' ? path : path === '/' ? targetRoot : `${targetRoot}${path}`;
  return `${targetPath}${window.location.search}${window.location.hash}`;
}

/* One registry for every behaviour that binds to DOM the Theme Editor can throw
   away. Shopify replaces a whole section's markup on `shopify:section:load`, and
   a handler bound at parse time stays attached to the discarded nodes — the
   merchant then changes a setting and the drawer, the cards and the variant
   picker stop responding inside the very tool used to accept the work. Each
   initialiser takes the root it should scan, runs once over the document at
   startup, and runs again over each reloaded section. */

const sectionInitialisers = [];

function onSection(init) {
  sectionInitialisers.push(init);
  init(document);
}

document.addEventListener('shopify:section:load', (event) => {
  sectionInitialisers.forEach((init) => init(event.target));
});

onSection(function initLanguageSwitchers(root) {
  root.querySelectorAll('[data-lang-switch]').forEach((switcher) => {
    switcher.addEventListener('click', (event) => {
      const option = event.target.closest('[name="language_code"]');
      if (!option) return;

      rememberLocaleChoice(option.value);

      const previewUrl = previewLanguageUrl(switcher, option);
      if (previewUrl) {
        event.preventDefault();
        window.location.assign(previewUrl);
      }
    });
  });
});

/* A re-rendered header starts expanded; give it the state the sentinel last
   reported, which the document element keeps. */
onSection(function syncCondensedHeader(root) {
  const header = root.querySelector('[data-header]');
  if (header) header.classList.toggle('is-condensed', document.documentElement.classList.contains('header-condensed'));
});

/* Runs after the switchers are bound: its synthetic click must reach their
   handler, which rewrites the URL on *.shopifypreview.com share links. */
(function matchBrowserLanguage() {
  const config = window.theme || {};
  /* Never navigate the Theme Editor preview away from the locale being edited. */
  if (window.Shopify && window.Shopify.designMode) return;
  if (!config.autoLocale || !document.querySelector('[data-lang-switch]')) return;

  let alreadyDecided = null;
  try {
    alreadyDecided = localStorage.getItem(LOCALE_CHOICE_KEY);
  } catch (error) {
    return;
  }
  if (alreadyDecided) return;

  const options = Array.from(document.querySelectorAll('[name="language_code"]'));
  const preferred = (navigator.languages || [navigator.language]).filter(Boolean);

  const match = preferred
    .map((tag) => {
      const base = tag.toLowerCase().split('-')[0];
      return options.find((option) => option.value.toLowerCase().split('-')[0] === base);
    })
    .find(Boolean);

  if (!match || match.value === config.currentLocale) {
    rememberLocaleChoice(config.currentLocale || 'kept');
    return;
  }

  rememberLocaleChoice(match.value);
  match.click();
})();

/* Auto-contrast corner badge. Sample the clean media only where the wordmark
   sits, rather than averaging the whole photograph. The merchant can tune the
   threshold live or force either logo variant for an exceptional crop. */
function watermarkPositionRatio(value, axis) {
  const keywordRatios = axis === 'x'
    ? { left: 0, center: .5, right: 1 }
    : { top: 0, center: .5, bottom: 1 };
  const token = String(value || 'center').toLowerCase();
  if (Object.prototype.hasOwnProperty.call(keywordRatios, token)) return keywordRatios[token];
  if (token.endsWith('%')) return Math.min(1, Math.max(0, parseFloat(token) / 100));
  return .5;
}

function watermarkObjectPosition(value) {
  const tokens = String(value || '50% 50%').trim().split(/\s+/);
  if (tokens.length === 1) tokens.push('50%');
  return {
    x: watermarkPositionRatio(tokens[0], 'x'),
    y: watermarkPositionRatio(tokens[1], 'y')
  };
}

function watermarkSourceRegion(media, badge) {
  const mediaRect = media.getBoundingClientRect();
  const badgeRect = badge.getBoundingClientRect();
  const naturalWidth = media.naturalWidth || media.videoWidth;
  const naturalHeight = media.naturalHeight || media.videoHeight;
  if (!mediaRect.width || !mediaRect.height || !naturalWidth || !naturalHeight) return null;

  /* The lower half is the contrast-sensitive wordmark; the crest remains
     coloured in both variants. Insets avoid transparent badge padding. */
  const sample = {
    x: badgeRect.left - mediaRect.left + badgeRect.width * .08,
    y: badgeRect.top - mediaRect.top + badgeRect.height * .45,
    width: badgeRect.width * .84,
    height: badgeRect.height * .42
  };
  const style = getComputedStyle(media);
  const fit = style.objectFit || 'fill';

  if (fit === 'fill') {
    return {
      x: sample.x * naturalWidth / mediaRect.width,
      y: sample.y * naturalHeight / mediaRect.height,
      width: sample.width * naturalWidth / mediaRect.width,
      height: sample.height * naturalHeight / mediaRect.height
    };
  }

  let scale = fit === 'cover'
    ? Math.max(mediaRect.width / naturalWidth, mediaRect.height / naturalHeight)
    : Math.min(mediaRect.width / naturalWidth, mediaRect.height / naturalHeight);
  if (fit === 'none') scale = 1;
  if (fit === 'scale-down') scale = Math.min(1, scale);

  const renderedWidth = naturalWidth * scale;
  const renderedHeight = naturalHeight * scale;
  const position = watermarkObjectPosition(style.objectPosition);
  const offsetX = (mediaRect.width - renderedWidth) * position.x;
  const offsetY = (mediaRect.height - renderedHeight) * position.y;
  const source = {
    x: (sample.x - offsetX) / scale,
    y: (sample.y - offsetY) / scale,
    width: sample.width / scale,
    height: sample.height / scale
  };

  const x2 = Math.min(naturalWidth, source.x + source.width);
  const y2 = Math.min(naturalHeight, source.y + source.height);
  source.x = Math.max(0, source.x);
  source.y = Math.max(0, source.y);
  source.width = x2 - source.x;
  source.height = y2 - source.y;
  return source.width > 0 && source.height > 0 ? source : null;
}

function watermarkLocalBrightness(media, badge) {
  const source = watermarkSourceRegion(media, badge);
  if (!source) return null;
  const canvas = document.createElement('canvas');
  canvas.width = 12;
  canvas.height = 12;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  try {
    context.drawImage(
      media,
      source.x,
      source.y,
      source.width,
      source.height,
      0,
      0,
      canvas.width,
      canvas.height
    );
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    let brightness = 0;
    let samples = 0;
    for (let index = 0; index < pixels.length; index += 4) {
      if (pixels[index + 3] === 0) continue;
      brightness += pixels[index] * .2126 + pixels[index + 1] * .7152 + pixels[index + 2] * .0722;
      samples += 1;
    }
    return samples ? brightness / samples / 255 : null;
  } catch (error) {
    return null;
  }
}

function watermarkMedia(watermark) {
  return watermark.closest('.watermarked-media')?.querySelector(':scope > img, :scope > video') || null;
}

function watermarkMediaReady(media) {
  return media instanceof HTMLVideoElement
    ? media.readyState >= 2
    : media.complete && media.naturalWidth > 0;
}

function updateWatermarkBadge(watermark) {
  const media = watermarkMedia(watermark);
  /* Measure whichever tone is showing: the other one is display: none, and a
     zero-sized box would read as "unavailable" and pin the badge to light. */
  const badge = Array.from(watermark.querySelectorAll('.media-watermark__badge'))
    .find((candidate) => candidate.getClientRects().length > 0);
  /* Media that has not arrived yet is sampled by its own ready listener; reading
     it now would report "unavailable" and flip the badge until then. */
  if (!media || !badge || !watermarkMediaReady(media)) return;
  const brightness = watermarkLocalBrightness(media, badge);
  /* Theme Editor previews can render Shopify CDN media from a different
     origin. If pixel sampling is unavailable, fail safe to the white-wordmark
     variant instead of leaving black lettering on a dark photograph. */
  if (brightness === null) {
    watermark.dataset.watermarkBadgeTone = 'light';
    watermark.dataset.watermarkBadgeBrightness = 'unavailable';
    return;
  }
  const threshold = Number(watermark.dataset.watermarkBadgeThreshold || 45) / 100;
  watermark.dataset.watermarkBadgeTone = brightness < threshold ? 'light' : 'dark';
  watermark.dataset.watermarkBadgeBrightness = String(Math.round(brightness * 100));
}

function updateAutoWatermarks(root = document) {
  root.querySelectorAll('[data-watermark-badge-auto]').forEach(updateWatermarkBadge);
}

/* Each sample is a canvas read per badge. Mobile browsers fire resize for every
   URL-bar show/hide while scrolling, so wait for the viewport to settle. */
let watermarkResizeTimer = 0;
window.addEventListener('resize', () => {
  clearTimeout(watermarkResizeTimer);
  watermarkResizeTimer = window.setTimeout(() => updateAutoWatermarks(), 150);
}, { passive: true });

onSection(function initAutoWatermarks(root) {
  root.querySelectorAll('[data-watermark-badge-auto]').forEach((watermark) => {
    const media = watermarkMedia(watermark);
    if (!media) return;
    const readyEvent = media instanceof HTMLVideoElement ? 'loadeddata' : 'load';
    media.addEventListener(readyEvent, () => updateWatermarkBadge(watermark));
    if (watermarkMediaReady(media)) {
      requestAnimationFrame(() => updateWatermarkBadge(watermark));
    }
  });
});

/* Native dialog owns focus trapping and Escape; clone the existing watermark
   rather than introducing a second overlay or exposing an unwatermarked image. */
onSection(function initProductZoom(root) {
  root.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const dialog = productRoot.querySelector('[data-product-zoom]');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.bound) return;
    const stage = dialog.querySelector('[data-product-zoom-stage]');
    const closeButton = dialog.querySelector('[data-product-zoom-close]');
    if (!stage || !closeButton) return;
    dialog.dataset.bound = 'true';
    productRoot.classList.add('product-zoom-ready');
    let opener = null;
    const sizeZoomImage = () => {
      const image = stage.querySelector('.product-zoom__media > img');
      if (image && dialog.open) image.sizes = `${Math.ceil(image.parentElement.getBoundingClientRect().width)}px`;
    };
    window.addEventListener('resize', sizeZoomImage);

    productRoot.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-product-zoom-trigger]');
      if (!trigger || !productRoot.contains(trigger) || dialog.open) return;
      const source = trigger.closest('.product__media');
      const sourceImage = source?.querySelector(':scope > img');
      if (!sourceImage) return;
      const media = source.cloneNode(true);
      media.className = 'product-zoom__media watermarked-media';
      media.querySelector('[data-product-zoom-trigger]')?.remove();
      const image = media.querySelector(':scope > img');
      const width = sourceImage.naturalWidth || Number(sourceImage.getAttribute('width'));
      const height = sourceImage.naturalHeight || Number(sourceImage.getAttribute('height'));
      media.style.setProperty('--zoom-ratio', width > 0 && height > 0 ? width / height : 1);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('fetchpriority');
      image.loading = 'eager';
      image.addEventListener('load', () => updateAutoWatermarks(dialog), { once: true });
      // Reuse the cached photo, then let srcset choose the right HD candidate.
      image.src = sourceImage.currentSrc || sourceImage.src;
      stage.replaceChildren(media);
      opener = trigger;
      dialog.showModal();
      document.documentElement.classList.add('product-zoom-open');
      sizeZoomImage();
      image.srcset = sourceImage.srcset;
      requestAnimationFrame(() => updateAutoWatermarks(dialog));
    });

    closeButton.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === stage) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('product-zoom-open');
      stage.replaceChildren();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    });
    const section = productRoot.closest('.shopify-section') || productRoot;
    section.addEventListener('shopify:section:unload', () => {
      window.removeEventListener('resize', sizeZoomImage);
      if (dialog.open) dialog.close();
      document.documentElement.classList.remove('product-zoom-open');
    });
  });
});

onSection(function initBrandVideo(root) {
  root.querySelectorAll('[data-brand-video]').forEach((dialog) => {
    if (dialog.dataset.bound || typeof dialog.showModal !== 'function') return;
    const section = dialog.closest('.shopify-section') || dialog.parentElement;
    const trigger = section.querySelector('[data-brand-video-trigger]');
    const video = dialog.querySelector('video');
    const error = dialog.querySelector('[data-brand-video-error]');
    if (!trigger || !video) return;
    dialog.dataset.bound = 'true';
    const showControls = () => { video.controls = true; };
    let revealingControls = false;
    video.addEventListener('pointerdown', () => { revealingControls = !video.controls; });
    video.addEventListener('click', (event) => {
      if (revealingControls || !video.controls) {
        event.preventDefault();
        showControls();
      }
      revealingControls = false;
    });
    ['focus', 'pause', 'ended'].forEach((event) => video.addEventListener(event, showControls));
    let transition = null;
    const animateSurface = () => {
      transition?.cancel();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
      transition = dialog.querySelector('.brand-video__surface').animate(
        [{ opacity: 0, transform: 'scale(.98)' }, { opacity: 1, transform: 'scale(1)' }],
        { duration: 200, easing: 'ease-out' }
      );
      return transition;
    };
    const close = () => {
      video.pause();
      // Close immediately, including while the opening animation is running.
      transition?.cancel();
      dialog.close();
    };
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (dialog.open) return;
      error.hidden = true;
      showControls();
      dialog.showModal();
      document.documentElement.classList.add('brand-video-open');
      if (!video.getAttribute('src')) {
        video.poster = video.dataset.poster;
        video.src = video.dataset.src;
      } else if (video.error) {
        /* A failed source never fires `error` again on its own; retry it so the
           message reappears if the network is still down. */
        video.load();
      }
      animateSurface();
      const play = video.play();
      play?.then(() => {
        if (!dialog.open) video.pause();
      }).catch(showControls);
    });
    video.addEventListener('error', () => { error.hidden = false; });
    dialog.querySelector('[data-brand-video-close]').addEventListener('click', close);
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) video.controls = false;
    });
    dialog.addEventListener('close', () => {
      video.pause();
      transition?.cancel();
      document.documentElement.classList.remove('brand-video-open');
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
    });
    section.addEventListener('shopify:section:unload', () => {
      video.pause();
      transition?.cancel();
      if (dialog.open) dialog.close();
      document.documentElement.classList.remove('brand-video-open');
    });
  });
});

/* Collapsible cards. One owner for every panel that can fold to an edge tab —
   the offer card and the hero copy panel both run through here. Collapsing is a
   preference for ordinary cards, so it persists. A card can instead opt into
   opening fresh on every page load while still respecting a manual close for
   the rest of that visit. */

/* A narrow screen has room for exactly one of these open at a time. Whatever is
   in storage, two open cards is not a layout the page can render — they land on
   top of each other over the hero. So on a phone the cards form an exclusive
   group: opening one parks the rest. Registration order decides the tie at load,
   and the membership card is registered last, so it wins by default. */
const collapsibleCards = [];
const narrowViewport = window.matchMedia(
  '(max-width: 640px), (max-height: 520px) and (orientation: landscape)'
);

onSection(function initCollapsibles(root) {
  /* A re-rendered section leaves its old entry pointing at a detached node; the
     mutual-exclusion pass would then park a card that no longer exists. */
  for (let i = collapsibleCards.length - 1; i >= 0; i -= 1) {
    if (!document.contains(collapsibleCards[i].promo)) collapsibleCards.splice(i, 1);
  }

  root.querySelectorAll('[data-collapsible]').forEach((promo) => {
  const panel = promo.querySelector('[data-collapsible-panel]');
  const collapseButton = promo.querySelector('[data-collapsible-collapse]');
  const tab = promo.querySelector('[data-collapsible-expand]');
  if (!panel || !tab) return;

  /* Scoped to the viewport class. A phone and a desktop give this card two
     different jobs, so a choice made on one must not decide the other — a stored
     "open" from a desktop visit was otherwise beating the phone default and
     putting both cards on the hero at once. */
  const narrow = narrowViewport.matches;
  const storageKey =
    'savory-crest:card:' + (promo.dataset.collapsibleId || 'default') + (narrow ? ':narrow' : '');
  const defaultOpen = promo.dataset.collapsibleDefaultOpen === 'true';
  const reopenWithHero = promo.dataset.collapsibleReopenWithHero === 'true';

  function setCollapsed(collapsed, persist) {
    /* A Theme Editor re-render leaves this card's window listeners behind; a
       detached card must not park the live ones that replaced it. */
    if (!promo.isConnected) return;
    promo.classList.toggle('is-collapsed', collapsed);
    panel.inert = collapsed;
    tab.hidden = !collapsed;
    /* Both controls describe the same panel; their state must never diverge. */
    tab.setAttribute('aria-expanded', String(!collapsed));
    if (collapseButton) collapseButton.setAttribute('aria-expanded', String(!collapsed));

    /* Read live: a rotated phone or a resized window changes how many cards fit. */
    if (!collapsed && narrowViewport.matches) {
      collapsibleCards.forEach((other) => {
        if (other.promo !== promo && !other.promo.classList.contains('is-collapsed')) {
          other.park();
        }
      });
    }

    if (!persist) return;
    try {
      localStorage.setItem(storageKey, collapsed ? 'collapsed' : 'open');
    } catch (error) {
      /* Storage can be unavailable; the control still works for this visit. */
    }
  }

  collapsibleCards.push({
    promo,
    park: () => setCollapsed(true, false),
    /* Only for the narrow-screen fallback below; runs after this card's state
       is fully initialised. */
    wasDismissed: () => dismissed,
    open: () => {
      parkedByScroll = false;
      armedFrom = window.scrollY;
      setCollapsed(false, false);
    }
  });

  let stored = null;
  try {
    stored = localStorage.getItem(storageKey);
  } catch (error) {
    stored = null;
  }

  /* A phone shows one card over the hero photograph, and that card is the
     membership offer. The intro panel yields the band and opens as its tab, so
     both are reachable and neither covers the other. The membership card can
     explicitly reset to open on each page load; ordinary cards still honour a
     stored choice. A reset-to-open card that is told to yield (the membership
     while it cannot be bought) starts folded instead, on every screen size. */
  const startsCollapsed =
    promo.dataset.collapsibleStartCollapsed === 'true' ||
    (promo.dataset.collapsibleMobileStartCollapsed === 'true' && narrow);

  /* Apply the opening state without playing the transition. Otherwise a card
     that opens collapsed animates open-to-closed on first paint, sweeping across
     the hero before folding away. */
  const opensCollapsed = defaultOpen
    ? startsCollapsed
    : stored
      ? stored === 'collapsed'
      : startsCollapsed;

  if (opensCollapsed) {
    panel.style.transition = 'none';
    setCollapsed(true, false);
    requestAnimationFrame(() => {
      panel.style.transition = '';
    });
  } else {
    setCollapsed(false, false);
  }

  /* Two different reasons a card can be shut, and they must not be confused.
     Dismissed: the visitor pressed the close control, and that decision sticks.
     Parked: scrolling folded it away, which is reversible — coming back to the
     top brings it out again along the path it left by. A card that merely opens
     parked (the intro on a phone) is neither, so returning to the top never
     drags it back over the card that took its place. */
  let dismissed = opensCollapsed && stored === 'collapsed';
  let parkedByScroll = false;
  /* Downward travel is measured from wherever the card was last opened, so
     reopening it re-arms the gesture instead of disabling it for the visit. */
  let armedFrom = window.scrollY;

  if (collapseButton) {
    collapseButton.addEventListener('click', () => {
      setCollapsed(true, !defaultOpen);
      dismissed = true;
      parkedByScroll = false;
      tab.focus();
    });
  }

  tab.addEventListener('click', () => {
    setCollapsed(false, !defaultOpen);
    dismissed = false;
    parkedByScroll = false;
    armedFrom = window.scrollY;
    if (collapseButton) collapseButton.focus();
  });

  if (promo.dataset.collapseOnScroll !== 'true') return;

  const COLLAPSE_TRAVEL = 240;
  const REOPEN_ABOVE = 80;
  let lastY = window.scrollY;
  let ticking = false;

  function heroIsMeaningfullyVisible() {
    const hero = document.querySelector('.hero');
    if (!hero) return false;
    const heroBounds = hero.getBoundingClientRect();
    const stickyHeader = document.querySelector('[data-header]');
    const visibleTop = Math.max(0, stickyHeader ? stickyHeader.getBoundingClientRect().bottom : 0);
    const visibleBottom = Math.min(window.innerHeight, heroBounds.bottom);
    const visibleHeight = Math.max(0, visibleBottom - Math.max(visibleTop, heroBounds.top));
    const reopenThreshold = Math.min(160, heroBounds.height * 0.25);
    return visibleHeight >= reopenThreshold;
  }

  /* Browsers restore scroll before or around `pageshow`. A default-open card
     must not remain over catalogue content just because its starting point was
     captured after that restoration. Reconcile once the restored geometry is
     available, while leaving a manually dismissed card alone. */
  function reconcileRestoredScroll() {
    if (dismissed || !reopenWithHero || promo.classList.contains('is-collapsed')) return;
    if (heroIsMeaningfullyVisible()) {
      armedFrom = Math.min(armedFrom, window.scrollY);
      return;
    }
    parkedByScroll = true;
    setCollapsed(true, false);
  }

  requestAnimationFrame(reconcileRestoredScroll);
  window.addEventListener('pageshow', () => requestAnimationFrame(reconcileRestoredScroll), { once: true });

  window.addEventListener(
    'scroll',
    () => {
      if (ticking || dismissed || !promo.isConnected) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY;
        const goingDown = y > lastY;
        const goingUp = y < lastY;
        lastY = y;

        if (!parkedByScroll) {
          if (promo.classList.contains('is-collapsed')) return;
          if (reopenWithHero) {
            if (heroIsMeaningfullyVisible()) return;
            parkedByScroll = true;
            setCollapsed(true, false);
            return;
          }
          if (goingUp) {
            armedFrom = Math.min(armedFrom, y);
            return;
          }
          if (!goingDown) return;
          if (y - armedFrom < COLLAPSE_TRAVEL) return;
          parkedByScroll = true;
          setCollapsed(true, false);
          return;
        }

        if (reopenWithHero) {
          if (!goingUp || !heroIsMeaningfullyVisible()) return;
        } else if (y > REOPEN_ABOVE) {
          return;
        }
        parkedByScroll = false;
        armedFrom = y;
        setCollapsed(false, false);
      });
    },
    { passive: true }
  );
  });

  /* Every card yielding on a phone would leave the hero with no words at all.
     Hand the band to the first card the visitor has not closed themselves. */
  if (narrowViewport.matches && collapsibleCards.every((card) => card.promo.classList.contains('is-collapsed'))) {
    collapsibleCards.find((card) => !card.wasDismissed())?.open();
  }
});

/* Narrow screens have no margin beside the body copy, so the parked membership
   tab sits on top of it. While the hero is out of view the tab steps aside (the
   header keeps its Membership link) and comes back with the hero. One observer,
   re-pointed when the Theme Editor replaces the hero section. */
let heroPassedObserver = null;

onSection(function initHeroPassed() {
  const hero = document.querySelector('.hero');
  if (!('IntersectionObserver' in window)) return;
  heroPassedObserver?.disconnect();
  if (!hero) {
    document.documentElement.classList.remove('hero-passed');
    return;
  }
  heroPassedObserver = new IntersectionObserver(([entry]) => {
    document.documentElement.classList.toggle('hero-passed', !entry.isIntersecting);
  });
  heroPassedObserver.observe(hero);
});

/* Pair the floating card with the hero panel.
   The two are read as one pair, but they live in different coordinate systems:
   the hero panel is centred inside its section, the floating card inside the
   viewport. A static CSS offset cannot reconcile them, because the hero's own
   top depends on the announcement bar, the masthead height, and whether the
   hero is taller than the viewport — at 1440x900 they landed 158px apart.
   So the card's centre is pinned to the hero panel's centre while the hero is
   on screen, clamped so the card never slides under the masthead or off the
   bottom, and released back to the viewport centre once the hero is gone. */

/* Both elements are re-queried on every run: either section can be replaced
   independently, and a stale reference pins the card to a node that is no longer
   on the page. */
let pairedCard = null;
let pairedHeroCard = null;
let pairListenersBound = false;

{
  const stacked = narrowViewport;
  let pairTicking = false;
  let cardObserver = null;

  function syncCardToHero() {
    if (!pairedCard || !pairedHeroCard) return;
    const heroSection = document.querySelector('.hero');
    const panel = pairedCard.querySelector('[data-collapsible-panel]');
    if (!panel) return;

    /* On a phone the intro panel is collapsed, so its box has been translated
       off screen and is useless as an anchor — measure the hero photograph
       itself. On a wider screen the two cards must line up with each other, so
       the intro panel is the anchor. */
    const anchor = stacked.matches && heroSection ? heroSection : pairedHeroCard;
    const hero = anchor.getBoundingClientRect();
    const height = panel.getBoundingClientRect().height;
    const viewport = window.innerHeight;

    if (hero.bottom <= 0 || hero.top >= viewport) {
      pairedCard.style.removeProperty('--promo-top');
      return;
    }

    const header = document.querySelector('.shopify-section-group-header-group');
    const gap = 16;
    const floor = (header ? header.getBoundingClientRect().bottom : 0) + gap + height / 2;
    const ceiling = viewport - gap - height / 2;
    const wanted = hero.top + hero.height / 2;

    pairedCard.style.setProperty(
      '--promo-top',
      Math.round(Math.min(Math.max(wanted, floor), Math.max(floor, ceiling))) + 'px'
    );
  }

  function requestCardSync() {
    if (pairTicking) return;
    pairTicking = true;
    requestAnimationFrame(() => {
      pairTicking = false;
      syncCardToHero();
    });
  }

  onSection(function initCardPairing() {
    pairedCard = document.querySelector('.promo:not(.promo--editor)');
    pairedHeroCard = document.querySelector('.hero__card');
    if (!pairedCard || !pairedHeroCard) return;

    syncCardToHero();

  /* The first run happens before the hero photograph has loaded, so it measures
     a hero that is not yet its final height — it pinned the card 146px high and
     then never corrected, because nothing scrolled and a `load` listener
     attached after `load` had already fired never runs. A ResizeObserver is the
     right instrument: it fires on the image landing, a font swap, a Theme Editor
     section re-render, and any later reflow. It is re-pointed on every run so a
     re-rendered hero is watched instead of its detached predecessor. */
    if ('ResizeObserver' in window) {
      if (!cardObserver) cardObserver = new ResizeObserver(requestCardSync);
      cardObserver.disconnect();
      cardObserver.observe(pairedHeroCard);
      cardObserver.observe(document.body);
    }

    /* The hero photograph settling is the single largest layout change between
       the first measurement and the truth. Its own load event is not
       frame-driven, so it corrects the pin even if the observer has not run
       yet. */
    const heroImage = document.querySelector('.hero__media img');
    if (heroImage && !heroImage.complete) {
      heroImage.addEventListener('load', syncCardToHero, { once: true });
    }

    if (pairListenersBound) return;
    pairListenersBound = true;
    window.addEventListener('scroll', requestCardSync, { passive: true });
    window.addEventListener('resize', requestCardSync);
    if (!('ResizeObserver' in window)) window.addEventListener('load', syncCardToHero);
  });
}

/* Reveal-on-entry hides its targets in CSS, so anything that arrives after the
   initial query stays invisible unless it is observed too. The Theme Editor
   re-renders a whole section on every setting change, which produced sections a
   merchant could not see or accept. Observe each newly rendered section, and in
   design mode show its content immediately rather than waiting for a scroll that
   may never come inside the editor's frame. */

let revealObserver = null;

function observeReveals(root) {
  const targets = (root || document).querySelectorAll('[data-reveal], [data-scroll-stage]');
  if (!targets.length) return;

  if (window.Shopify && window.Shopify.designMode) {
    targets.forEach((target) => target.classList.add('is-visible'));
    return;
  }

  if (!revealObserver) return;
  targets.forEach((target) => revealObserver.observe(target));
}

if ('IntersectionObserver' in window) {
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );
  onSection(observeReveals);
} else {
  onSection((root) =>
    root.querySelectorAll('[data-reveal], [data-scroll-stage]').forEach((target) => target.classList.add('is-visible'))
  );
}

/* Vertical page rail. It maps the current document into viewport-sized pages,
   then mirrors natural scrolling instead of taking the wheel away from the
   visitor. The final marker always maps to the real document bottom, including
   when the last page is shorter than a viewport. */

const pageRail = document.querySelector('[data-page-rail]');

if (pageRail && !(window.Shopify && window.Shopify.designMode)) {
  const pageRailList = pageRail.querySelector('[data-page-rail-list]');
  const pageRailStatus = pageRail.querySelector('[data-page-rail-status]');
  const reducedPageMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const maximumRailPages = 24;
  let railPages = 0;
  let activeRailPage = -1;
  let railFrame = 0;
  /* A rail click marks its target at once; while the smooth scroll travels
     there, the pages it passes must not steal the marker back. */
  let pendingRailPage = -1;
  let pendingRailUntil = 0;

  function railText(template, index, total) {
    return template.replace('__INDEX__', String(index + 1)).replace('__TOTAL__', String(total));
  }

  function maximumPageScroll() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  function railTarget(index) {
    if (railPages <= 1) return 0;
    return maximumPageScroll() * (index / (railPages - 1));
  }

  function setActiveRailPage(index, announce) {
    if (index === activeRailPage || index < 0 || index >= railPages) return;
    activeRailPage = index;

    pageRailList.querySelectorAll('[data-page-rail-step]').forEach((step, stepIndex) => {
      const current = stepIndex === index;
      step.classList.toggle('is-current', current);
      if (current) step.setAttribute('aria-current', 'page');
      else step.removeAttribute('aria-current');
    });

    if (announce && pageRailStatus) {
      pageRailStatus.textContent = railText(pageRail.dataset.pageStatus, index, railPages);
    }
  }

  function updatePageRail() {
    railFrame = 0;
    const maxScroll = maximumPageScroll();
    if (maxScroll < window.innerHeight * 0.75) return;
    const progress = Math.min(1, Math.max(0, window.scrollY / maxScroll));
    const index = Math.round(progress * (railPages - 1));
    if (pendingRailPage !== -1) {
      if (index !== pendingRailPage && performance.now() < pendingRailUntil) return;
      pendingRailPage = -1;
    }
    setActiveRailPage(index, false);
  }

  function rebuildPageRail() {
    const maxScroll = maximumPageScroll();
    const nextPageCount = Math.min(
      maximumRailPages,
      Math.max(2, Math.ceil(maxScroll / Math.max(window.innerHeight, 1)) + 1)
    );
    const shouldShow = maxScroll >= window.innerHeight * 0.75;
    pageRail.hidden = !shouldShow;
    if (!shouldShow) return;

    if (nextPageCount !== railPages) {
      railPages = nextPageCount;
      activeRailPage = -1;
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < railPages; index += 1) {
        const step = document.createElement('button');
        step.className = 'page-rail__step';
        step.type = 'button';
        step.dataset.pageRailStep = '';
        step.dataset.pageIndex = String(index);
        step.setAttribute('aria-label', railText(pageRail.dataset.pageLabel, index, railPages));
        fragment.appendChild(step);
      }

      pageRailList.replaceChildren(fragment);
    }

    updatePageRail();
  }

  pageRailList.addEventListener('click', (event) => {
    const step = event.target.closest('[data-page-rail-step]');
    if (!step) return;
    const index = Number(step.dataset.pageIndex);
    setActiveRailPage(index, true);
    pendingRailPage = index;
    pendingRailUntil = performance.now() + 1000;
    window.scrollTo({
      top: railTarget(index),
      behavior: reducedPageMotion.matches ? 'auto' : 'smooth'
    });
  });

  window.addEventListener(
    'scroll',
    () => {
      if (railFrame) return;
      railFrame = requestAnimationFrame(updatePageRail);
    },
    { passive: true }
  );
  window.addEventListener('resize', rebuildPageRail);
  window.addEventListener('pageshow', rebuildPageRail);

  if ('ResizeObserver' in window) {
    const pageRailObserver = new ResizeObserver(rebuildPageRail);
    pageRailObserver.observe(document.body);
  }

  rebuildPageRail();
}

/* Live queries rather than captured node lists: the header is the section a
   merchant edits most, and every edit replaces these nodes. */
const drawerButton = () => document.querySelector('[data-menu-toggle]');
const drawerPanel = () => document.querySelector('[data-mobile-nav]');
const allSubmenuToggles = () => Array.from(document.querySelectorAll('[data-submenu-toggle]'));

function setDrawer(open) {
  const button = drawerButton();
  const panel = drawerPanel();
  if (!button || !panel) return;
  button.setAttribute('aria-expanded', String(open));
  panel.hidden = !open;
  if (open) {
    /* The drawer sits after every header action in the DOM; without an explicit
       move a keyboard user tabs through the whole header before reaching it. */
    const first = panel.querySelector('a[href], button:not([hidden])');
    if (first) first.focus();
  }
}

/* Click outside the open drawer closes it. Focus only returns to the toggle when
   it was inside the drawer, so a click on page content keeps its own target. */
document.addEventListener('click', (event) => {
  const button = drawerButton();
  if (!button || button.getAttribute('aria-expanded') !== 'true') return;
  if (event.target.closest('[data-mobile-nav], [data-menu-toggle]')) return;
  const panel = drawerPanel();
  const hadFocus = panel && panel.contains(document.activeElement);
  setDrawer(false);
  if (hadFocus) button.focus();
});

function closeSubmenus(except) {
  allSubmenuToggles().forEach((toggle) => {
    if (toggle === except) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  });
}

onSection(function initHeaderControls(root) {
  const button = root.querySelector('[data-menu-toggle]');
  if (button) {
    button.addEventListener('click', () => {
      setDrawer(button.getAttribute('aria-expanded') !== 'true');
    });
  }

  root.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      closeSubmenus(toggle);
      toggle.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
    });
  });
});

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-submenu-toggle], [data-submenu]')) closeSubmenus();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const openToggle = allSubmenuToggles().find(
    (toggle) => toggle.getAttribute('aria-expanded') === 'true'
  );

  if (openToggle) {
    closeSubmenus();
    openToggle.focus();
    return;
  }

  const button = drawerButton();
  if (button && button.getAttribute('aria-expanded') === 'true') {
    setDrawer(false);
    button.focus();
  }
});

onSection(function initVariantPickers(root) {
  root.querySelectorAll('[data-product-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const button = select.closest('form')?.querySelector('[data-product-submit]');
      if (button) {
        button.disabled = option.disabled || button.dataset.membershipInCart === 'true';
        button.textContent = option.disabled ? button.dataset.soldOutLabel : button.dataset.availableLabel;
      }
      const price = option.dataset.price;
      const comparePrice = option.dataset.comparePrice;
      const priceNode = select.closest('[data-product-root]')?.querySelector('[data-product-price]');
      if (!priceNode) return;

      const priceElement = document.createElement('span');
      priceElement.textContent = price || '';
      const priceElements = [priceElement];

      if (comparePrice) {
        const compareElement = document.createElement('span');
        compareElement.className = 'price__compare';
        compareElement.textContent = comparePrice;
        priceElements.push(compareElement);
      }

      priceNode.replaceChildren(...priceElements);
    });
  });
});

/* A variant with its own photo leads the gallery while it is selected. The
   original order is restored first, so at most two slots trade places, and the
   pair also trade `sizes` because the lead slot renders larger. */
onSection(function initVariantMedia(root) {
  root.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const select = productRoot.querySelector('[data-product-select]');
    const slots = Array.from(productRoot.querySelectorAll('.product__gallery > [data-media-id]'));
    if (!select || slots.length < 2) return;
    const image = (slot) => slot.querySelector(':scope > img');
    const home = slots.map((slot) => ({ slot, parent: slot.parentElement, sizes: image(slot)?.getAttribute('sizes') }));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    select.addEventListener('change', () => {
      const mediaId = select.options[select.selectedIndex]?.dataset.mediaId;
      const target = mediaId ? slots.find((slot) => slot.dataset.mediaId === mediaId) : null;
      const lead = slots[0];
      if (target && home[0].parent.firstElementChild === target) return;

      home.forEach(({ slot, parent, sizes }) => {
        parent.append(slot);
        if (sizes) image(slot)?.setAttribute('sizes', sizes);
      });
      if (!target || target === lead) return;

      const marker = document.createComment('');
      target.replaceWith(marker);
      lead.replaceWith(target);
      marker.replaceWith(lead);
      const leadSizes = image(lead)?.getAttribute('sizes');
      const targetSizes = image(target)?.getAttribute('sizes');
      if (leadSizes && targetSizes) {
        image(target).setAttribute('sizes', leadSizes);
        image(lead).setAttribute('sizes', targetSizes);
      }
      if (!reducedMotion.matches) {
        image(target)?.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
      updateAutoWatermarks(productRoot);
    });
  });
});

/* Sticky purchase bar. Shown only while the product's own submit button is off
   screen, so there is never a second copy of the same action in view. It
   submits the main form through its `form` attribute and mirrors that button's
   state after a variant change (the picker above updates the main button first,
   because its listener was bound earlier). */
onSection(function initStickyPurchase(root) {
  root.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const bar = productRoot.querySelector('[data-product-sticky]');
    const mainButton = productRoot.querySelector('[data-product-submit]');
    if (!bar || !mainButton || !('IntersectionObserver' in window)) return;
    const stickyButton = bar.querySelector('[data-product-sticky-submit]');
    const stickyPrice = bar.querySelector('[data-product-sticky-price]');

    bar.hidden = false;
    document.documentElement.classList.add('has-product-sticky');

    const observer = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting);
    });
    observer.observe(mainButton);

    productRoot.querySelector('[data-product-select]')?.addEventListener('change', () => {
      if (stickyButton) {
        stickyButton.disabled = mainButton.disabled;
        stickyButton.textContent = mainButton.textContent.trim();
      }
      const price = productRoot.querySelector('[data-product-price] > span');
      if (stickyPrice && price) stickyPrice.textContent = price.textContent;
    });

    const section = productRoot.closest('.shopify-section') || productRoot;
    section.addEventListener('shopify:section:unload', () => {
      observer.disconnect();
      document.documentElement.classList.remove('has-product-sticky');
    });
  });
});
